import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Drawer, TextField, Typography } from "@mui/material";
import FilterList from "@mui/icons-material/FilterList";
import { ChannelList, useChatContext } from "stream-chat-react";
import { useFilters } from "../../hooks/ui";
import useDisclosure from "../../hooks/ui/useDisclosure";
import api, { serializeSearchParams } from "../../utils/api";
import { apiUrls } from "../../utils/urls";
import { messageConversationFilters } from "./conversationFilters";
import MissingCoachingChats from "./MissingCoachingChats";
import { FILTERED_CHANNEL_PAGE_SIZE, queryChannelsByIds } from "./queryFilteredChannels";

const sort = [{ pinned_at: -1 }, { last_message_at: -1 }];
const options = { limit: 30 };

function UnfilteredChannelList({ streamUser }) {
  const filters = {
    type: "messaging",
    members: { $in: [streamUser.id] },
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        "& .str-chat__channel-list": { width: "100%", height: "100%" },
        "& .str-chat__channel-list-messenger": { width: "100%", height: "100%" },
        "& .str-chat__channel-list-messenger-react": {
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
        "& .str-chat__channel-list-messenger-react__main": {
          width: "100%",
          height: "100%",
          overflow: "auto",
        },
        "& .str-chat__channel-list-messenger__main": { width: "100%" },
        "& .str-chat__channel-preview-messenger": { width: "100%" },
      }}
    >
      <ChannelList
        filters={filters}
        sort={sort}
        options={options}
        setActiveChannelOnMount={false}
        showChannelSearch
      />
    </Box>
  );
}

function EmptyFilteredChannels({ matchingStudentCount = 0, missingStudentCount = 0 }) {
  let message = "No conversations match these filters.";
  if (matchingStudentCount > 0 && missingStudentCount === 0) {
    message = "Matching students were found, but none of their conversations include you.";
  }
  return (
    <Typography sx={{ p: 2 }} color="text.secondary">
      {message}
    </Typography>
  );
}

function FilteredChannelList({ streamUser, channels, matchingStudentCount, missingStudentCount }) {
  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  const customQueryChannels = useCallback(async ({ currentChannels, queryType, setChannels, setHasNextPage }) => {
    const source = channelsRef.current || [];
    const offset = queryType === "reload" ? 0 : currentChannels.length;
    const page = source.slice(offset, offset + FILTERED_CHANNEL_PAGE_SIZE);
    setChannels(queryType === "reload" ? page : [...currentChannels, ...page]);
    setHasNextPage(offset + page.length < source.length);
  }, []);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        "& .str-chat__channel-list": { width: "100%", height: "100%" },
        "& .str-chat__channel-list-messenger": { width: "100%", height: "100%" },
        "& .str-chat__channel-list-messenger-react": {
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
        "& .str-chat__channel-list-messenger-react__main": {
          width: "100%",
          height: "100%",
          overflow: "auto",
        },
        "& .str-chat__channel-list-messenger__main": { width: "100%" },
        "& .str-chat__channel-preview-messenger": { width: "100%" },
      }}
    >
      <ChannelList
        customQueryChannels={customQueryChannels}
        filters={{ type: "messaging", members: { $in: [String(streamUser.id)] } }}
        sort={sort}
        options={options}
        setActiveChannelOnMount={false}
        allowNewMessagesFromUnfilteredChannels={false}
        EmptyStateIndicator={() => (
          <EmptyFilteredChannels
            matchingStudentCount={matchingStudentCount}
            missingStudentCount={missingStudentCount}
          />
        )}
      />
    </Box>
  );
}

function channelMatchesSearch(channel, query) {
  const name = String(channel.data?.name || "").toLowerCase();
  if (name.includes(query)) {
    return true;
  }
  const members = Object.values(channel.state?.members || {});
  return members.some((member) => {
    const memberName = String(member.user?.name || "").toLowerCase();
    const memberEmail = String(member.user?.email || "").toLowerCase();
    return memberName.includes(query) || memberEmail.includes(query);
  });
}

function EmployeeChannelSidebar({ streamUser }) {
  const { client } = useChatContext();
  const filterDisc = useDisclosure();
  const { Filters, FiltersAppliedList, getFilterParams } = useFilters({
    name: "Message Conversations",
    saveState: false,
    filtersConfig: messageConversationFilters,
    handleClose: filterDisc.onClose,
  });
  const filterParams = getFilterParams();
  const filterKey = JSON.stringify(filterParams);
  const hasFilters = filterKey !== "{}";
  const [refreshToken, setRefreshToken] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filteredChannels, setFilteredChannels] = useState([]);
  const [missingStudents, setMissingStudents] = useState([]);
  const [matchingStudentCount, setMatchingStudentCount] = useState(0);
  const [localSearch, setLocalSearch] = useState("");

  useEffect(() => {
    setLocalSearch("");
  }, [filterKey]);

  useEffect(() => {
    if (!hasFilters) {
      setLoading(false);
      setError(null);
      setFilteredChannels([]);
      setMissingStudents([]);
      setMatchingStudentCount(0);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(apiUrls.messaging.channelFilters(), {
          params: serializeSearchParams({
            search_params: JSON.parse(filterKey),
          }),
        });
        const data = response.data || {};
        const channels = await queryChannelsByIds(client, {
          channelIds: data.channel_ids || [],
          memberId: streamUser.id,
        });
        if (cancelled) {
          return;
        }
        setFilteredChannels(channels);
        setMissingStudents(data.students_without_coaching_chat || []);
        setMatchingStudentCount(data.matching_student_count || 0);
      } catch (err) {
        if (cancelled) {
          return;
        }
        setFilteredChannels([]);
        setMissingStudents([]);
        setMatchingStudentCount(0);
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to filter conversations."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, filterKey, hasFilters, refreshToken, streamUser]);

  const visibleChannels = useMemo(() => {
    const query = localSearch.trim().toLowerCase();
    if (!query) {
      return filteredChannels;
    }
    return filteredChannels.filter((channel) => channelMatchesSearch(channel, query));
  }, [filteredChannels, localSearch]);

  return (
    <>
      <Drawer
        anchor="right"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        ModalProps={{ disableEnforceFocus: true }}
        {...filterDisc.getDisclosureProps()}
      >
        <Box sx={{ width: { xs: "100vw", sm: "25rem" }, maxWidth: "100vw" }}>
          <Filters />
        </Box>
      </Drawer>
      <Box sx={{ px: 1, pt: 1, flexShrink: 0 }}>
        <Button
          size="small"
          startIcon={<FilterList />}
          onClick={filterDisc.onOpen}
        >
          Filters
        </Button>
        <FiltersAppliedList />
      </Box>
      {hasFilters && (
        <Box sx={{ px: 1, pb: 1, flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search these conversations"
            value={localSearch}
            onChange={(event) => setLocalSearch(event.target.value)}
          />
        </Box>
      )}
      {hasFilters && (
        <MissingCoachingChats
          students={loading ? [] : missingStudents}
          streamUserId={streamUser.id}
          onCreated={() => setRefreshToken((value) => value + 1)}
        />
      )}
      {hasFilters && loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {hasFilters && error && (
        <Typography sx={{ px: 2, py: 1 }} color="error">
          {error}
        </Typography>
      )}
      {hasFilters && !loading && !error && (
        <FilteredChannelList
          key={`${filterKey}:${localSearch}:${visibleChannels.length}:${refreshToken}`}
          streamUser={streamUser}
          channels={visibleChannels}
          matchingStudentCount={matchingStudentCount}
          missingStudentCount={missingStudents.length}
        />
      )}
      {!hasFilters && <UnfilteredChannelList streamUser={streamUser} />}
    </>
  );
}

export default function ChannelSidebar({ streamUser, isEmployee }) {
  return (
    <Box
      sx={{
        width: { xs: "100%", sm: 360 },
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      {isEmployee ? (
        <EmployeeChannelSidebar streamUser={streamUser} />
      ) : (
        <UnfilteredChannelList streamUser={streamUser} />
      )}
    </Box>
  );
}
